import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const formatSupabaseError = (err, fallback) => {
  return [err?.message, err?.details, err?.hint].filter(Boolean).join(' ') || fallback;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'bg-green-50 text-green-700 border-green-100';
    case 'intake_submitted':
    case 'legal_triage':
    case 'ready_for_attorney_review':
    case 'attorney_review':
    case 'more_information_needed':
      return 'bg-amber-50 text-amber-700 border-amber-100';
    case 'attorney_review_complete':
    case 'ready_for_document_generation':
    case 'documents_generated':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    default:
      return 'bg-blue-50 text-blue-700 border-blue-100';
  }
};

const getStatusLabel = (status) => {
  if (status === 'matter_created') return 'plan created';
  if (status === 'ready_for_attorney_review') return 'ready for attorney review';
  if (status === 'attorney_review') return 'attorney review in progress';
  if (status === 'more_information_needed') return 'waiting for additional information';
  if (status === 'attorney_review_complete') return 'attorney review complete';
  if (status === 'ready_for_document_generation') return 'ready for document generation';
  if (status === 'documents_generated') return 'documents generated';
  return status?.replace('matter', 'plan').replaceAll('_', ' ');
};

const LawFirmMatterList = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMatters = async () => {
      if (!profile?.law_firm_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchErr } = await supabase
          .from('matters')
          .select(`
            id,
            plan_type,
            status,
            complexity_level,
            client_state,
            assigned_attorney_id,
            created_at,
            client:clients (
              first_name,
              last_name,
              email,
              phone,
              state_of_residence
            )
          `)
          .eq('law_firm_id', profile.law_firm_id)
          .order('created_at', { ascending: false });

        if (fetchErr) throw fetchErr;
        setMatters(data || []);
      } catch (err) {
        console.error('Law firm matter list error:', err);
        setError(formatSupabaseError(err, 'Failed to load assigned plans.'));
      } finally {
        setLoading(false);
      }
    };

    fetchMatters();
  }, [profile]);

  if (!profile?.law_firm_id) {
    return (
      <div className="p-8 text-center bg-white rounded-lg border border-slate-200">
        Law firm access requires a law firm profile assignment.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Assigned Plans</h1>
          <p className="text-sm text-slate-500">Estate planning matters assigned to your law firm.</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mx-auto h-10 w-10 text-slate-300 mb-4" />
            <p className="text-slate-500">Loading assigned plans...</p>
          </div>
        ) : matters.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Plan Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Complexity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                  <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {matters.map((matter) => (
                  <tr
                    key={matter.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/law-firm/matters/${matter.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">
                        {matter.client?.first_name} {matter.client?.last_name}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-[220px]">{matter.client?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-700 font-medium">{matter.plan_type}</div>
                      <div className="text-xs text-slate-400">{matter.client_state || matter.client?.state_of_residence || 'State not set'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full border capitalize ${getStatusColor(matter.status)}`}>
                        {getStatusLabel(matter.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-xs text-slate-600 bg-slate-100 py-0.5 px-2 rounded-md border border-slate-200 capitalize">
                        {matter.complexity_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {format(new Date(matter.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-slate-400 hover:text-slate-900 p-1">
                        <SafeIcon icon={FiIcons.FiChevronRight} className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-20 text-center text-slate-500 px-4">
            <SafeIcon icon={FiIcons.FiInbox} className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No assigned plans</h3>
            <p className="mt-1 text-slate-500">Plans assigned to your law firm will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LawFirmMatterList;
